import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import { MODULE_ROUTES } from 'constants/routes';

interface IPrivateRouteProps {
  children: JSX.Element;
}

let applicationModulesCache: string[] | null = null;

const PrivateRoute: React.FC<IPrivateRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [allowedModules, setAllowedModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasModuleAccess, setHasModuleAccess] = useState(true);
  const notificationShown = useRef(false);
  const token = Helpers.getToken();
  const urlToken = searchParams.get('token');

  const isAuthenticated = (token && !Helpers.isObjEmpty(token)) || urlToken;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    if (applicationModulesCache) {
      setAllowedModules(applicationModulesCache);
      setLoading(false);
      return;
    }

    ApiService.getUserApplicationModules()
      .then((response) => {
        const modules = response.data.data.map(
          (item: { module_name: string }) => item.module_name
        );

        applicationModulesCache = modules;

        setAllowedModules(modules);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (loading) {
      return;
    }

    const currentPath = location.pathname;

    const requiredModule = Object.keys(MODULE_ROUTES).find((module) =>
      MODULE_ROUTES[module as keyof typeof MODULE_ROUTES].includes(currentPath)
    );

    // Route is not restricted
    if (!requiredModule) {
      setHasModuleAccess(true);
      return;
    }

    const access = allowedModules.includes(requiredModule);

    setHasModuleAccess(access);

    if (!access && !notificationShown.current) {
      notificationShown.current = true;

      notification.error({
        message: t('ACCESS_DENIED'),
        description: t('YOU_DO_NOT_HAVE_ACCESS_TO_THIS_MODULE')
      });
    }
  }, [location.pathname, allowedModules, loading, t]);

  if (!isAuthenticated) {
    return <Navigate to='/' replace />;
  }
  if (loading) {
    return null;
  }
  if (!hasModuleAccess) {
    return <Navigate to='/working_area' replace />;
  }
  return <>{children}</>;
};

export default PrivateRoute;
